import {Fragment, useEffect, useState} from 'react'

import {Dialog, Menu, Transition} from '@headlessui/react'

import sdk from "scoutli-sdk"

import {
    BadgeCheckIcon,
    BellIcon,
    CalendarIcon,
    ChartSquareBarIcon,
    ChatIcon,
    CurrencyDollarIcon,
    DatabaseIcon, DocumentReportIcon,
    EyeIcon,
    HomeIcon,
    MailIcon,
    MenuAlt2Icon,
    SparklesIcon,
    UserAddIcon,
    XIcon,
    UserIcon
} from '@heroicons/react/outline'

import logo from "../../assets/logo.png"

import {Link, useLocation, useNavigate} from "react-router-dom";

const navigation = [
    {name: 'Home', href: '/', icon: HomeIcon},
    {name: 'Scouting', href: '/scouting', icon: EyeIcon},
    {name: 'Calendar', href: '/calendar', icon: CalendarIcon},
    {name: 'Scrims', href: '/scrims', icon: MailIcon},
    {name: 'Financial', href: '/financial', icon: CurrencyDollarIcon},
    {name: 'Player Data', href: '/data', icon: DatabaseIcon},
    {name: 'Reports', href: '/reports', icon: DocumentReportIcon},
    {name: 'Job Board', href: '/jobs', icon: UserAddIcon},
    {name: 'Social Feed', href: '/social', icon: ChatIcon, space: true},
    {name: 'Fantasy League', href: '/fantasy', icon: SparklesIcon},
    {name: "Pick'em Challenge", href: '/challenge', icon: BadgeCheckIcon},
    {name: "Analytics", href: '/analytics', icon: ChartSquareBarIcon}
]

const userNavigation = [
    {name: 'Ayarlar', href: '/settings'},
    {name: 'Çıkış Yap', href: '/logout'}
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function Dashboard({title, content, extra}) {
    const [isSidebarOpen, setSidebarOpen] = useState(false)
    const [picture, setPicture] = useState(0)

    const location = useLocation();
    const navigate = useNavigate();

    navigation.forEach(navigate => {
        navigate.current = location.pathname === navigate.href;
    })

    const accessToken = localStorage.getItem("accessToken");

    useEffect(() => {
        async function check() {
            let loggedIn = false

            if (accessToken) {
                let user = await sdk.user(accessToken).get.userData().catch(() => {
                    localStorage.removeItem("accessToken")
                })

                if (user) {
                    loggedIn = true
                    setPicture(await sdk.user(accessToken).get.profilePicture())
                }
            }

            if (!loggedIn) {
                return navigate("/")
            }
        }

        check()
    })

    // anything

    return (
        <>
            <Transition.Root show={isSidebarOpen} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 z-40 flex md:hidden" onClose={setSidebarOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75"/>
                    </Transition.Child>
                    <Transition.Child
                        as={Fragment}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                    >
                        <div className="relative max-w-xs w-full bg-white pt-5 pb-4 flex-1 flex flex-col">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-in-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in-out duration-300"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="absolute top-0 right-0 -mr-12 pt-2">
                                    <button
                                        type="button"
                                        className="ml-1 flex items-center justify-center h-10 w-10 rounded-full"
                                        onClick={() => setSidebarOpen(false)}
                                    >
                                        <span className="sr-only">Menüyü kapat</span>
                                        <XIcon className="h-6 w-6 text-white" aria-hidden="true"/>
                                    </button>
                                </div>
                            </Transition.Child>
                            <div className="flex-shrink-0 px-4 flex items-center">
                                <img
                                    className="h-8 w-auto"
                                    src={logo}
                                    alt="Scoutli"
                                />
                                <h1 className="ml-2 text-xl text-primary font-semibold tracking-wider">Scoutli</h1>
                            </div>
                            <div className="mt-5 flex-1 h-0 overflow-y-auto">
                                <nav className="px-2">
                                    {navigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={classNames(
                                                item.current
                                                    ? 'bg-gray-100 text-gray-900'
                                                    : 'text-gray-600 hover:bg-gray-50 transition-colors hover:text-gray-900',
                                                `group mb-2 rounded-md py-2 px-2 flex items-center text-base font-medium ${item.space ? "mt-6" : ""}`
                                            )}
                                        >
                                            <item.icon
                                                className={classNames(
                                                    item.current
                                                        ? 'text-gray-500'
                                                        : 'text-gray-400 group-transition-colors hover:text-gray-500',
                                                    'mr-4 flex-shrink-0 h-6 w-6'
                                                )}
                                                aria-hidden="true"
                                            />
                                            {item.name}
                                        </Link>
                                    ))}
                                </nav>
                            </div>
                        </div>
                    </Transition.Child>
                    <div className="flex-shrink-0 w-14"/>
                </Dialog>
            </Transition.Root>

            <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
                <div className="border-r border-gray-100 pt-5 flex flex-col flex-grow bg-white overflow-y-auto">
                    <div className="flex-shrink-0 px-4 flex items-center">
                        <img
                            className="h-8 w-auto"
                            src={logo}
                            alt="Scoutli"
                        />
                        <h1 className="ml-2 text-xl text-primary font-semibold tracking-wider">Scoutli</h1>
                    </div>
                    <div className="flex-grow mt-5 flex flex-col">
                        <nav className="flex-1 px-2 pb-4">
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={classNames(
                                        item.current
                                            ? 'bg-gray-100 text-gray-900'
                                            : 'text-gray-600 hover:bg-gray-50 transition-colors hover:text-gray-900',
                                        `group mb-2 rounded-md py-2 px-2 flex items-center text-sm font-medium ${item.space ? "mt-6" : ""}`
                                    )}
                                >
                                    <item.icon
                                        className={classNames(
                                            item.current
                                                ? 'text-gray-500'
                                                : 'text-gray-400 group-transition-colors hover:text-gray-500',
                                            'mr-3 flex-shrink-0 h-6 w-6'
                                        )}
                                        aria-hidden="true"
                                    />
                                    {item.name}
                                </Link>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            {extra}

            <div className={`flex flex-col ${extra ? "md:pl-[32rem]" : "md:pl-64"}`}>
                <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white">
                    <button
                        type="button"
                        className="px-4 text-gray-500 md:hidden"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <span className="sr-only">Menüyü aç</span>
                        <MenuAlt2Icon className="h-6 w-6" aria-hidden="true"/>
                    </button>
                    <div className="flex-1 px-4 flex justify-end">
                        <div className="flex items-center">
                            <Link
                                to="/notifications"
                                className="bg-white p-1 rounded-full text-gray-400 transition-colors hover:text-gray-500"
                            >
                                <span className="sr-only">Bildirimleri görüntüle</span>
                                <BellIcon className="h-6 w-6" aria-hidden="true"/>
                            </Link>

                            <Menu as="div" className="ml-3 relative">
                                <div>
                                    <Menu.Button
                                        className="max-w-xs bg-white flex items-center text-sm rounded-full">
                                        <span className="sr-only">Menüyü aç</span>
                                        {picture === 0 && (
                                            <UserIcon className="h-6 w-6 text-gray-400 transition-colors hover:text-gray-500"/>
                                        )}

                                        {picture === null && (
                                            <UserIcon className="h-6 w-6 text-gray-400 transition-colors hover:text-gray-500"/>
                                        )}

                                        {picture !== 0 && picture !== null && (
                                            <img
                                                id="navPicture"
                                                className="h-8 w-8 rounded-full"
                                                src={picture}
                                            />
                                        )}
                                    </Menu.Button>
                                </div>
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                >
                                    <Menu.Items
                                        className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white">
                                        {userNavigation.map((item) => (
                                            <Menu.Item key={item.name}>
                                                {({active}) => (
                                                    <Link
                                                        to={item.href}
                                                        className={classNames(
                                                            active ? 'bg-gray-100' : '',
                                                            'block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50'
                                                        )}
                                                    >
                                                        {item.name}
                                                    </Link>
                                                )}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </div>
                    </div>
                </div>

                <main className="flex-1">
                    <div className="py-6">
                        <div className="mx-auto px-4 sm:px-6 md:px-8">
                            <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
                        </div>
                        <div className="mx-auto px-4 sm:px-6 md:px-8">
                            <div className="py-4">
                                {content}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    )
}
